import { Button, Dialog, DialogActions, DialogContent, DialogTitle, styled } from "@mui/material";
import { find, get, isEqual } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import "./DocumentDropComponent.css";
import DocumentList from "./DocumentList";
import DocumentListViewOnly from "./DocumentListViewOnly";
import DropzoneComponent from "./DropzoneComponent";

const StyledDocumentDropContainer = styled("div")({
    paddingTop: "8px",
    marginBottom: "20px",
});

const standardHeaders = [
    {
        name: "Name",
        prop: "documentName",
        type: "plaintext",
        extraHeaderClassName: "standardHeaderCol1",
    },
    {
        name: "Document Type",
        prop: "documentType",
        type: "plaintext",
        extraHeaderClassName: "standardHeaderCol2",
    },
    {
        name: "",
        prop: "",
        type: "plaintext",
        extraHeaderClassName: "standardHeaderCol3",
    },
];

const approvalHeaders = [
    {
        name: "",
        prop: "",
        type: "plaintext",
        extraHeaderClassName: "approvalHeaderCol1",
    },
    {
        name: "Name",
        prop: "documentName",
        type: "plaintext",
        extraHeaderClassName: "approvalHeaderCol2",
    },
    {
        name: "Document Type",
        prop: "documentType",
        type: "plaintext",
        extraHeaderClassName: "approvalHeaderCol3",
    },
    {
        name: "Approved By",
        prop: "",
        type: "plaintext",
        extraHeaderClassName: "approvalHeaderCol4",
    },
];

const DocumentDrop = (props) => {
    const {
        allDocumentTypes,
        addDocuments,
        approveDocument,
        columnToSort,
        documentsUploading,
        documentTypes,
        downloadDocument,
        downloadView,
        entityKey,
        errors,
        formDisabled,
        handleDocumentSort,
        handleRejectedDocs,
        includeSource,
        noHeader,
        parentType,
        removeDocument,
        resetRequiredMissingDocuments,
        requiredDocs,
        role,
        savedDocuments,
        setAdminDocumentTypeListAvailable,
        sortDirection,
        stateCode,
        terminationDocument,
        toggleTerminationDoc,
        type,
        viewDocument,
        viewOnly,
        documentRequirements,
        documentAttachmentPermissions,
    } = props;

    const [anchorEl, setAnchorEl] = useState(null);
    const [docKey, setDocKey] = useState(null);
    const [adminListReady, setAdminListReady] = useState(false);
    const [missingDocs, setMissingDocs] = useState([]);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);

    const prevProps = useRef({ savedDocuments, requiredDocs });

    useEffect(() => {
        return () => {
            resetRequiredMissingDocuments();
        };
    }, []);

    useEffect(() => {
        adminDocumentTypeListAvailable();
    }, [documentRequirements]);

    useEffect(() => {
        if (
            !isEqual(savedDocuments, prevProps.current.savedDocuments) ||
            !isEqual(requiredDocs, prevProps.current.requiredDocs)
        ) {
            determineMissingRequiredDocuments();
        }
        prevProps.current = { savedDocuments, requiredDocs };
    }, [savedDocuments, requiredDocs]);

    const handleOpenDocumentMenu = (x, anchorElArg) => {
        setAnchorEl(anchorElArg);
        setDocKey(x);
    };

    const toggleDeleteDialog = (open, docKey) => {
        if (open) {
            handleMenuClose();
            setDeleteDialog(true);
            setDocToDelete(docKey);
        } else {
            setDeleteDialog(false);
            setDocToDelete(null);
        }
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const deleteAndMenuClose = () => {
        setAnchorEl(null);
        removeDocument(docToDelete);
        toggleDeleteDialog(false);
    };

    const previewAndMenuClose = (docKey, url) => {
        setAnchorEl(null);
        viewDocument(docKey, url);
    };

    const handleAddDocuments = (accepted, rejected, ownerType) => {
        addDocuments(entityKey, type, accepted, ownerType, parentType);
        if (rejected.length > 0) {
            handleRejectedDocs(rejected);
        }
    };

    const handleApproveDocumentAndClose = (docKey) => {
        setAnchorEl(null);
        approveDocument(docKey);
    };

    const adminDocumentTypeListAvailable = () => {
        if (parentType) {
            setAdminDocumentTypeListAvailable();
            setAdminListReady(true);
        }
    };

    const determineMissingRequiredDocuments = () => {
        const docTypeRequiredList = requiredDocs ?? [];

        const fulfilledDocs = docTypeRequiredList
            .map((requiredDocument) => {
                const savedDocumentMatch = find(savedDocuments, (savedDoc) => {
                    return requiredDocument.documentTypeKey === get(savedDoc, "documentType.documentTypeKey");
                });

                return savedDocumentMatch ? get(savedDocumentMatch, "documentType.documentTypeKey") : null;
            })
            .filter((doc) => doc);

        const missingDocsLocal = docTypeRequiredList.filter(
            (document) => !fulfilledDocs.includes(document.documentTypeKey)
        );
        setMissingDocs(missingDocsLocal);
    };

    const renderHelperMessage = (type) => {
        return (
            <React.Fragment>
                {type === "comp" && (
                    <div className="helperText">For best results, the Marketing Flyer Photo should be 1224x440px.</div>
                )}
                {type === "voucher" && (
                    <div className="helperText">
                        You will be paid fastest if you attach your Commission Payment or Equivalent
                    </div>
                )}
                {type === "property" && (
                    <div className="helperText">
                        Preferred specs for Primary Photos:
                        <div className="helperTextFlexBox">
                            <div>4:3 Aspect Ratio</div>
                            <div>1440 x 1080 Pixels (Resolution)</div>
                        </div>
                    </div>
                )}
                {missingDocs.length > 0 && (
                    <div className="helperText missingDocumentsText">
                        <h4>Missing required documents for submission:</h4>
                        <div className="helperTextFlexBox">
                            {missingDocs.map((doc) => (
                                <div>{doc["name"]}</div>
                            ))}
                        </div>
                    </div>
                )}
            </React.Fragment>
        );
    };

    const open = Boolean(anchorEl);

    return viewOnly ? (
        <DocumentListViewOnly
            {...props}
            open={open}
            anchorEl={anchorEl}
            docMenuKey={docKey}
            documentTypes={documentTypes}
            handlePreviewDocument={previewAndMenuClose}
            handleDownloadDocument={downloadDocument}
            handleMenuClose={handleMenuClose}
            handleOpenDocumentMenu={handleOpenDocumentMenu}
            downloadView={downloadView === true}
            includeSource={includeSource}
            type={type}
            documentAttachmentPermissions={documentAttachmentPermissions}
        />
    ) : (
        <StyledDocumentDropContainer>
            {
                // No documents are attached
                Object.keys(savedDocuments).length === 0 && Object.keys(documentsUploading).length === 0 ? (
                    <React.Fragment>
                        <DropzoneComponent handleAddDocuments={handleAddDocuments} {...props} />

                        {renderHelperMessage(type)}
                    </React.Fragment>
                ) : (
                    // Documents are attached
                    (<React.Fragment>
                        <DocumentList
                            {...props}
                            handleAddDocuments={handleAddDocuments}
                            errors={errors}
                            header={
                                type === "user"
                                    ? standardHeaders
                                    : role === "portlandBrokerAdmin"
                                      ? approvalHeaders
                                      : standardHeaders
                            }
                            handleSort={handleDocumentSort}
                            columnToSort={columnToSort}
                            sortDirection={sortDirection}
                            allDocumentTypes={allDocumentTypes}
                            documentTypes={documentTypes}
                            handleOpenDocumentMenu={handleOpenDocumentMenu}
                            handleMenuClose={handleMenuClose}
                            toggleDeleteDialog={toggleDeleteDialog}
                            handlePreviewDocument={previewAndMenuClose}
                            anchorEl={anchorEl}
                            open={open}
                            docMenuKey={docKey}
                            savedDocuments={savedDocuments}
                            documentsUploading={documentsUploading}
                            formDisabled={formDisabled}
                            role={role}
                            approveDocument={handleApproveDocumentAndClose}
                            docApproved={Boolean(get(savedDocuments, docKey + ".approvedBy", null) != null)}
                            toggleTerminationDoc={toggleTerminationDoc}
                            terminationDocument={terminationDocument}
                            parentEntityType={parentType}
                            stateCode={stateCode}
                            adminListReady={adminListReady}
                            noHeader={noHeader}
                            documentAttachmentPermissions={documentAttachmentPermissions}
                            documentRequirements={documentRequirements}
                        />
                        <DropzoneComponent handleAddDocuments={handleAddDocuments} {...props} />
                        {renderHelperMessage(type)}
                    </React.Fragment>)
                )
            }
            <Dialog open={deleteDialog} maxWidth="sm" aria-labelledby="confirmation-dialog-title">
                <DialogTitle style={{ textAlign: "center" }} id="confirmation-dialog-title">
                    Delete Document?
                </DialogTitle>
                <DialogContent style={{ textAlign: "center" }}>
                    <div>Are you sure you want to delete this document? </div>
                    <div>This change cannot be undone </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => toggleDeleteDialog(false, docToDelete)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={() => deleteAndMenuClose()} color="primary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </StyledDocumentDropContainer>
    );
};

export default DocumentDrop;
